import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiChatsCircleLight, PiPaperPlaneTiltLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import GlassCard from '../shared/brand/GlassCard';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { formatAbsoluteTime, formatRelativeTime } from '../shared/feed/feedTime';
import {
  EngagementMessage,
  EngagementRole,
  errorMessage,
  listMessages,
  sendMessage,
} from '../../services/api/marketplaceService';

const POLL_MS = 15000;
const MAX_MESSAGE = 5000;

/**
 * The private thread between a client and the lawyer they hired. Used on both
 * sides; `as` is the side this page acts as (the server checks it). Polls while
 * the page is visible; no live socket yet.
 */
const EngagementConversation: React.FC<{
  leadUuid: string;
  as: EngagementRole;
  canMessage: boolean;
  /** The other party, for the empty state ("Write to Kari"). */
  otherName?: string;
}> = ({ leadUuid, as, canMessage, otherName }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [messages, setMessages] = useState<EngagementMessage[] | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  // Each load and send bumps this; a load that started before a newer load or
  // a send is stale and would drop the message just sent.
  const generation = useRef(0);

  const load = useCallback(async () => {
    const mine = ++generation.current;
    try {
      const fresh = await listMessages(leadUuid, as);
      if (mine === generation.current) setMessages(fresh);
    } catch {
      if (mine === generation.current) setMessages((m) => m ?? []);
    }
  }, [leadUuid, as]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messages?.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const sent = await sendMessage(leadUuid, as, body);
      generation.current += 1; // any load still in flight predates this message
      setMessages((m) => [...(m ?? []).filter((x) => x.uuid !== sent.uuid), sent]);
      setDraft('');
    } catch (err) {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: errorMessage(err, t('Could not send the message')),
      }));
    } finally {
      setSending(false);
    }
  };

  return (
    <GlassCard padding="none">
      <div className="max-h-[28rem] min-h-[8rem] space-y-3 overflow-y-auto px-4 py-4 custom-scrollbar-main"
        data-testid="marketplace-conversation">
        {messages === null ? (
          <Skeleton variant="list" rows={2} />
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center gap-1 py-6 text-center text-sm text-fg-muted">
            <PiChatsCircleLight className="h-7 w-7" />
            <span>
              {otherName
                ? t('No messages yet. Write to {{name}} here.', { name: otherName })
                : t('No messages yet.')}
            </span>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.uuid} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg border px-3 py-2 ${m.mine
                ? 'border-hairline bg-surface-sunken'
                : 'border-hairline-soft bg-surface'}`}>
                {/* Plain text, never HTML: the other party wrote it. */}
                <p className="whitespace-pre-wrap break-words text-sm text-fg-body">{m.body}</p>
                <p className="mt-1 text-right text-[10px] text-fg-muted" title={formatAbsoluteTime(m.created_at ?? undefined)}>
                  {formatRelativeTime(m.created_at ?? undefined, t)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t border-hairline-soft px-4 py-3">
        {canMessage ? (
          <div className="space-y-2">
            <Textarea
              id={`marketplace-message-${as}`}
              label={t('Message')}
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE))}
            />
            <div className="flex justify-end">
              <Button variant="primary" size="small" onClick={send} isLoading={sending}
                disabled={sending || !draft.trim()} icon={<PiPaperPlaneTiltLight className="h-3.5 w-3.5" />}>
                {t('Send')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-fg-muted">{t('This engagement is closed. The conversation is read-only.')}</p>
        )}
      </div>
    </GlassCard>
  );
};

export default EngagementConversation;
